import { Autocomplete, Box, Button, Grid, TextField, Typography } from '@mui/material';
import { FastField, Field } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import { NContact } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import { queryParams } from '@shared/commonFunctions';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { footerLenderSelector } from '@store/reducers/dealCalculatorSlice';
import { Wrapper } from '@styles/CommonStyles';
import { ControlsGroup, Label, LabelGroup } from '@styles/ContactManager/DealCalculator/FiltersStyles';
import { SalesTaxField } from '@styles/ContactManager/DealCalculator/LenderSectionStyles';

import { EditProductModal } from '../../../components/Deals/components/Products/EditProducts/EditProductModal';
import { SalesTaxInfoModal } from '../../../components/Deals/components/SalesTaxInfoModal';
import { CoApplicant, IContactData } from '../../../components/Deals/interfaces';
import { ILenderFooterState } from '../interfaces';
import { IGlobalInputs } from '../models/GlobalInputsModel';
import { SwapConfirmationModal } from './SwapConfirmationModal';

interface IDealCalculatorFilterProps {
  assetId?: string;
  leadId?: string;
  contactAddress?: NContact.Address;
  dealId?: string;
  onClose?: () => void;
  assetNumericId?: number;
  numericId?: number;
  globalInputs?: IGlobalInputs;
  isVoidEnable?: boolean;
  setFieldValue: <T>(field: string, value: T, shouldValidate?: boolean) => void;
  saveAndPullEligibleLenders?: () => void;
}

const getRates = () => {
  const arr = [];
  for (let i = 0; i <= 2.5; i = i + 0.25) {
    arr.push({ value: i, label: i.toFixed(2) + '%' });
  }
  return arr;
};

export const DealCalculatorFilters = ({
  assetId,
  leadId,
  dealId,
  onClose,
  globalInputs,
  assetNumericId,
  numericId,
  isVoidEnable,
  contactAddress,
  setFieldValue,
  saveAndPullEligibleLenders,
}: IDealCalculatorFilterProps) => {
  const { isAdmin } = useUserAccount();
  const [editProductPosition, setEditProductPosition] = useState(0);
  const footerLender: Partial<ILenderFooterState> | null = useSelector(footerLenderSelector);
  const [dealCalculatorLeadProductId, setLeadProductId] = useState(null);
  const [coBorrowerData, setCoBorrowerData] = useState<CoApplicant[]>([]);
  const contactId = queryParams()?.id;
  const { data: fetchedBorrowerData } = useSWR<IContactData>(`/${SERVICES.CONTACT}/contacts/${contactId}`);
  const [coBorrower, setCoBorrower] = useState<CoApplicant | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openSwapAlert, setOpenSwapAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  const { mutate: refreshGlobalProducts } = useSWR(`/${SERVICES.PRODUCT}/leads/dealcalculator?LeadId=${leadId}`, {
    revalidateOnMount: false,
  });

  useEffect(() => {
    if (fetchedBorrowerData) {
      const filterDroppedCoBorrower = fetchedBorrowerData?.coApplicants?.filter((coApplicant: CoApplicant) => !coApplicant?.isDroppedContact);
      setCoBorrowerData(filterDroppedCoBorrower);
      const selectedCoBorrower = fetchedBorrowerData?.coApplicants?.find(
        (applicant) => applicant.id === globalInputs?.coContactId
      );
      setCoBorrower(selectedCoBorrower || null);
    }
  }, [fetchedBorrowerData, globalInputs?.coContactId, dealId]);

  useEffect(() => {
    setFieldValue('globalInputs.additionalNotes', footerLender?.additionalNotes);
  }, [footerLender?.additionalNotes]);

  const handleChangeCoBorrower = (value: CoApplicant | null) => {
    setFieldValue('globalInputs.coContactId', value?.id);
    setCoBorrower(value);
    saveAndPullEligibleLenders?.();
  };

  const handleSwap = () => {
    setOpenSwapAlert(true);
  };

  const onGetPlanClick = useCallback(() => {
    if (globalInputs?.term) {
      setEditProductPosition((prevState) => prevState + 1);
    } else {
      Notistack.generalError('Please enter the term');
    }
  }, [globalInputs]);

  return (
    <Wrapper>
      <Grid
        container
        spacing={3}
      >
        <Grid
          item
          xs={12}
          lg={4}
        >
          <Typography variant="h3">Loan Details</Typography>
          <LoaderBackdrop open={loading} />

          <ControlsGroup>
            <Autocomplete
              disableClearable={false}
              style={{ minWidth: '220px' }}
              value={coBorrower}
              getOptionLabel={(option) => option.firstName + ' ' + option.lastName}
              options={coBorrowerData}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Co-Borrower"
                  style={{ height: '48px' }}
                />
              )}
              renderOption={(props, option) => {
                return (
                  <li
                    {...props}
                    key={option.id}
                  >
                    {option.firstName + ' ' + option.lastName}
                  </li>
                );
              }}
              onChange={(_, value) => {
                handleChangeCoBorrower(value);
              }}
            />
            <Button
              variant="text"
              disabled={!coBorrower}
              onClick={handleSwap}
            >
              Swap
            </Button>
            {openSwapAlert && (
              <SwapConfirmationModal
                assetId={assetId}
                leadId={leadId}
                dealId={dealId}
                openSwapAlert={openSwapAlert}
                assetNumericId={assetNumericId}
                numericId={numericId}
                setOpenSwapAlert={setOpenSwapAlert}
                setLoading={setLoading}
                borrower={{
                  id: fetchedBorrowerData?.id ?? '',
                  contactName: `${fetchedBorrowerData?.firstName ?? ''} ${fetchedBorrowerData?.lastName ?? ''}`,
                  numericId: fetchedBorrowerData?.numericId,
                }}
                coBorrower={{
                  id: coBorrower?.id ?? '',
                  contactName: `${coBorrower?.firstName ?? ''} ${coBorrower?.lastName ?? ''}`,
                  numericId: coBorrower?.numericId,
                }}
                onClose={onClose}
              />
            )}

            <FastField
              fullWidth
              label="Sales Price"
              name="globalInputs.salesPrice"
              prefix="$"
              component={NumberInputField}
              decimalScale={null}
            />
          </ControlsGroup>
        </Grid>

        <Grid
          item
          xs={12}
          lg={4}
        >
          <Typography variant="h3">Lender Inputs</Typography>

          <ControlsGroup>
            <FastField
              fullWidth
              label="Cash Out"
              name="globalInputs.cashOut"
              prefix="$"
              component={NumberInputField}
            />

            <FastField
              fullWidth
              label="Down Payment"
              name="globalInputs.downPayment"
              component={NumberInputField}
            />

            <FastField
              fullWidth
              label="Term"
              name="globalInputs.term"
              limit={3}
              component={NumberInputField}
            />
          </ControlsGroup>
        </Grid>

        <Grid
          item
          xs={12}
          lg={4}
        >
          <Typography variant="h3">Lender Results</Typography>

          <ControlsGroup>
            <Box width="200px">
              <FastField
                hideNoneOption
                label="Rate Markup"
                options={getRates()}
                name="globalInputs.rateMarkup"
                component={SelectAutocompleteField}
              />
            </Box>
            <LabelGroup>
              <Label>ACH</Label>
              <FastField
                label="Yes"
                name="globalInputs.isAch"
                component={CheckboxField}
              />
            </LabelGroup>

            <LabelGroup>
              <Field
                fullWidth
                label="Sales Tax"
                name="globalInputs.salesTaxPercentage"
                component={SalesTaxField}
                suffix="%"
                decimalScale={2}
                InputProps={{
                  endAdornment: (
                    <Button
                      style={{ width: 75 }}
                      variant="text"
                      onClick={() => setIsModalOpen(true)}
                    >
                      Select
                    </Button>
                  ),
                }}
              />
            </LabelGroup>

            <Button
              sx={{ minWidth: '100px' }}
              variant="contained"
              onClick={onGetPlanClick}
            >
              Get Plan
            </Button>
          </ControlsGroup>
        </Grid>
      </Grid>
      <EditProductModal
        isVoidEnable={isAdmin || isVoidEnable || false}
        editProductPosition={editProductPosition}
        setEditProductPosition={setEditProductPosition}
        reloadDeal={refreshGlobalProducts}
        contactAddress={contactAddress}
        preventDealSave={false}
        customData={{
          getRetailsFormUrl: `/${SERVICES.PRODUCT}/leads/dealcalculator?LeadId=${leadId}`,
          getTreeUrl: `/${SERVICES.PRODUCT}/leads/${leadId}/plans?termMonths=${globalInputs?.term}&&state=${contactAddress?.state}`,
          submitUrl: `/${SERVICES.PRODUCT}/leads/${leadId}/dealcalculator`,
          type: 'dealCalculator',
          data: {
            leadId,
            setLeadProductId,
            dealCalculatorLeadProductId,
          },
        }}
      />
      <SalesTaxInfoModal
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
      />
    </Wrapper>
  );
};
